import { mainnetPriceFeeds } from './priceFeeds';
/**
 * By modelling a tree where all the assets are our vertices and all the price feeds
 * are our path sections between those vertices, we can recursively traverse through to find the
 * shortest path between our origin asset and the destination asset.
 *
 * ### Example
 * ``` typescript
 * const feeds: readonly Feed[] = [
 *   {
 *     id: 0,
 *     from: 'ETH',
 *     to: 'USD',
 *     address: '',
 *     decimals: 1,
 *   },
 *   {
 *     id: 1,
 *     from: 'BTC',
 *     to: 'USD',
 *     address: '',
 *     decimals: 1,
 *   }
 * ];
 *
 * getShortestPath('ETH', 'BTC', feeds);
 * // => [
 * //   {
 * //     feedId: 0,
 * //     inverse: false,
 * //   },
 * //   {
 * //     feedId: 1,
 * //     inverse: true,
 * //   },
 * // ];
 * ```
 *
 * @param fromAsset The asset vertex to begin pathing from for this iteration
 * @param toAsset The destination asset vertex (the asset we ultimately wish to convert to)
 * @param feeds The feeds representing the paths between all vertices
 * @returns [[`Path`]] representing the shortest path between our origin and destination asset vertices
 */
export const getShortestPath = (fromAsset, toAsset, feeds = mainnetPriceFeeds) => getShortestPathRecursively(fromAsset, toAsset, feeds);
/**
 * @ignore
 * By modelling a tree where all the assets are our vertices and all the price feeds
 * are our paths between those vertices, we can recursively traverse through to find the
 * shortest path between our origin asset and the destination asset.
 * @param fromAsset The asset vertex to begin pathing from for this iteration
 * @param toAsset The destination asset vertex (the asset we ultimately wish to convert to)
 * @param feeds The feeds representing the paths between all vertices
 * @param currentPathArray An array of the [[`PathSection`]]s taken to reach this vertex
 * @returns [[`Path`]] representing the shortest path between our origin and destination asset vertices
 */
const getShortestPathRecursively = (fromAsset, toAsset, feeds, currentPathSectionArray = []) => {
    // Find all feeds containing our 'fromAsset' in their 'from' prop
    const matchingFromFeeds = getFeedsWhereFromMatches(fromAsset, feeds);
    // Find all feeds containing our 'fromAsset' in their 'to' prop
    const matchingToFeeds = getFeedsWhereToMatches(fromAsset, feeds);
    // No feed contains our 'fromAsset' so return empty array
    if (matchingFromFeeds.length === 0 && matchingToFeeds.length === 0) {
        return [];
    }
    const pathSectionsToTraverse = [
        ...matchingFromFeeds.map((feed) => ({
            feedId: feed.id,
            inverse: false,
        })),
        ...matchingToFeeds.map((feed) => ({
            feedId: feed.id,
            inverse: true,
        })),
    ];
    const matches = pathSectionsToTraverse.filter((pathSection) => getAssetOnOtherSideOfPathSection(pathSection, feeds) === toAsset);
    // Return match
    if (matches.length > 0) {
        return [...currentPathSectionArray, matches[0]];
    }
    // No matches - traverse the next descendants
    else {
        // Exclude feeds we have already found matches in
        const traversedFeedIdsThisRound = pathSectionsToTraverse.map((pathSection) => pathSection.feedId);
        const newFeeds = feeds.filter((feed) => !traversedFeedIdsThisRound.includes(feed.id));
        return (pathSectionsToTraverse
            .map((pathSection) => {
            const nextFromAsset = getAssetOnOtherSideOfPathSection(pathSection, feeds);
            return getShortestPathRecursively(nextFromAsset, toAsset, newFeeds, [
                ...currentPathSectionArray,
                pathSection,
            ]);
        })
            // Filter out any empty paths
            .filter((path) => path && path.length > 0)
            // Take the shortest path
            .reduce((previous, current) => {
            return previous.length > current.length || previous.length === 0
                ? current
                : previous;
        }, []));
    }
};
/**
 * @ignore
 * @param id
 * @param feeds
 */
const getFeedById = (id, feeds) => feeds.find((feed) => feed.id === id);
/**
 * @ignore
 * @param asset
 * @param feeds
 */
const getFeedsWhereFromMatches = (asset, feeds) => feeds.filter((feed) => feed.from === asset);
/**
 * @ignore
 * @param asset
 * @param feeds
 */
const getFeedsWhereToMatches = (asset, feeds) => feeds.filter((feed) => feed.to === asset);
/**
 * @ignore
 * @param pathSection
 * @param feeds
 */
const getAssetOnOtherSideOfPathSection = (pathSection, feeds) => {
    const feed = getFeedById(pathSection.feedId, feeds);
    return pathSection.inverse === false ? feed.to : feed.from;
};
//# sourceMappingURL=data:application/json;base64,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